import React from 'react';
import { Box, Drawer, List, ListItem, ListItemButton, ListItemIcon, ListItemText, Typography, Divider, useMediaQuery, useTheme, alpha } from '@mui/material';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import ArticleIcon from '@mui/icons-material/Article';
import InfoIcon from '@mui/icons-material/Info';
import FeedbackIcon from '@mui/icons-material/Feedback';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import AddLinkIcon from '@mui/icons-material/AddLink';
import BarChartIcon from '@mui/icons-material/BarChart';
import SearchIcon from '@mui/icons-material/Search';
import HubIcon from '@mui/icons-material/Hub';
import SignpostIcon from '@mui/icons-material/Signpost';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import BugReportIcon from '@mui/icons-material/BugReport';
import ScienceIcon from '@mui/icons-material/Science';
import IntegrationInstructionsIcon from '@mui/icons-material/IntegrationInstructions';
import InventoryIcon from '@mui/icons-material/Inventory';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import TravelExploreIcon from '@mui/icons-material/TravelExplore';
import GavelIcon from '@mui/icons-material/Gavel';
import PrivacyTipIcon from '@mui/icons-material/PrivacyTip';
import GitHubIcon from '@mui/icons-material/GitHub';
import { cyber } from '../../theme';

const drawerWidth = 260;

interface SidebarProps {
    mobileOpen?: boolean;
    onClose?: () => void;
}

interface NavItem {
    to: string;
    label: string;
    icon: React.ReactNode;
    secondary?: string;
    // Match nested routes (e.g. /analytics/:slug) when true
    prefixMatch?: boolean;
}

interface NavSection {
    title: string;
    items: NavItem[];
}

const navSections: NavSection[] = [
    {
        title: 'Intelligence',
        items: [
            { to: '/', label: 'Threat Reports', icon: <ArticleIcon /> },
            { to: '/briefing', label: 'AI Briefing Agent', icon: <SmartToyIcon />, secondary: 'Weekly threat summary' },
            { to: '/analytics', label: 'Agentic Reports', icon: <AnalyticsIcon />, secondary: 'AI-generated threat analysis', prefixMatch: true },
        ],
    },
    {
        title: 'Hunt & Explore',
        items: [
            { to: '/ioc-search', label: 'IOC Search', icon: <SearchIcon /> },
            { to: '/cve-search', label: 'CVE Search', icon: <BugReportIcon /> },
            { to: '/entities', label: 'Threat Entities', icon: <TravelExploreIcon />, secondary: 'Actor & malware profiles', prefixMatch: true },
            { to: '/stix-bundles', label: 'STIX Bundles', icon: <InventoryIcon /> },
            { to: '/knowledge-graph', label: 'STIX Constellation', icon: <HubIcon />, secondary: 'Knowledge graph explorer', prefixMatch: true },
            { to: '/statistics', label: 'Statistics', icon: <BarChartIcon /> },
        ],
    },
    {
        title: 'Platform',
        items: [
            { to: '/mcp-integration', label: 'MCP Server', icon: <IntegrationInstructionsIcon /> },
            { to: '/submit', label: 'Submit Article', icon: <AddLinkIcon /> },
            { to: '/research', label: 'Research Project', icon: <ScienceIcon /> },
            { to: '/roadmap', label: 'Roadmap', icon: <SignpostIcon /> },
            { to: '/about', label: 'About', icon: <InfoIcon /> },
        ],
    },
];

const navButtonSx = {
    mx: 1,
    my: 0.25,
    borderRadius: 2,
    color: 'text.secondary',
    '& .MuiListItemIcon-root': {
        minWidth: 40,
        color: 'text.secondary',
        transition: 'color 0.2s ease',
    },
    '&:hover': {
        backgroundColor: alpha(cyber.primary, 0.08),
        color: 'text.primary',
        '& .MuiListItemIcon-root': { color: cyber.primaryLight },
    },
    '&.Mui-selected': {
        backgroundColor: alpha(cyber.primary, 0.14),
        color: 'text.primary',
        boxShadow: `inset 3px 0 0 ${cyber.primary}`,
        '& .MuiListItemIcon-root': { color: cyber.primaryLight },
        '&:hover': {
            backgroundColor: alpha(cyber.primary, 0.2),
        },
    },
};

const Sidebar: React.FC<SidebarProps> = ({ mobileOpen = false, onClose }) => {
    const location = useLocation();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    const handleNavClick = () => {
        if (isMobile && onClose) {
            onClose();
        }
    };

    const isSelected = (item: NavItem) =>
        item.prefixMatch ? location.pathname.startsWith(item.to) : location.pathname === item.to;

    const drawerContent = (
        <>
            <Box>
                <Box sx={{ p: 2, display: 'flex', alignItems: 'center', height: '64px' }}>
                    <Box
                        component="img"
                        src="/TI-Mindmap-HUB-logo.png"
                        alt="TI Mindmap HUB Logo"
                        sx={{ height: 32, width: 32, mr: 1.5, filter: 'drop-shadow(0 0 8px rgba(123, 76, 255, 0.5))' }}
                    />
                    <Typography variant="h6" noWrap component="div" sx={{ fontWeight: 800, letterSpacing: '-0.02em', ...cyber.gradientText }}>
                        TI Mindmap HUB
                    </Typography>
                </Box>
                <Divider />
                {navSections.map((section) => (
                    <List key={section.title} dense disablePadding sx={{ pb: 1 }}>
                        <Typography
                            variant="overline"
                            sx={{ pl: 2.5, pt: 1.5, display: 'block', color: 'text.secondary', fontSize: '0.65rem', opacity: 0.8 }}
                        >
                            {section.title}
                        </Typography>
                        {section.items.map((item) => (
                            <ListItem key={item.to} disablePadding>
                                <ListItemButton
                                    component={RouterLink}
                                    to={item.to}
                                    selected={isSelected(item)}
                                    onClick={handleNavClick}
                                    sx={navButtonSx}
                                >
                                    <ListItemIcon>{item.icon}</ListItemIcon>
                                    <ListItemText
                                        primary={item.label}
                                        secondary={item.secondary}
                                        primaryTypographyProps={{ fontSize: '0.875rem', fontWeight: 500 }}
                                        secondaryTypographyProps={{
                                            variant: 'caption',
                                            sx: { fontSize: '0.65rem', opacity: 0.7 },
                                        }}
                                    />
                                </ListItemButton>
                            </ListItem>
                        ))}
                    </List>
                ))}
                <Divider />
                <List dense disablePadding sx={{ pb: 1 }}>
                    <Typography
                        variant="overline"
                        sx={{ pl: 2.5, pt: 1.5, display: 'block', color: 'text.secondary', fontSize: '0.65rem', opacity: 0.8 }}
                    >
                        Support & Links
                    </Typography>
                    <ListItem disablePadding>
                        <ListItemButton component={RouterLink} to="/feedback" selected={location.pathname === '/feedback'} onClick={handleNavClick} sx={navButtonSx}>
                            <ListItemIcon><FeedbackIcon /></ListItemIcon>
                            <ListItemText primary="Feedback" primaryTypographyProps={{ fontSize: '0.875rem', fontWeight: 500 }} />
                        </ListItemButton>
                    </ListItem>
                    <ListItem disablePadding>
                        <ListItemButton component="a" href="https://github.com/TI-Mindmap-HUB-Org/ti-mindmap-hub-research" target="_blank" rel="noopener noreferrer" sx={navButtonSx}>
                            <ListItemIcon><GitHubIcon /></ListItemIcon>
                            <ListItemText primary="GitHub Repository" primaryTypographyProps={{ fontSize: '0.875rem', fontWeight: 500 }} />
                        </ListItemButton>
                    </ListItem>
                    <ListItem disablePadding>
                        <ListItemButton component="a" href="https://docs.ti-mindmap-hub.com/" target="_blank" rel="noopener noreferrer" sx={navButtonSx}>
                            <ListItemIcon><MenuBookIcon /></ListItemIcon>
                            <ListItemText primary="Documentation" primaryTypographyProps={{ fontSize: '0.875rem', fontWeight: 500 }} />
                        </ListItemButton>
                    </ListItem>
                </List>
            </Box>

            {/* Legal and Disclaimer Section at the bottom */}
            <Box sx={{ marginTop: 'auto', p: 2 }}>
                <List dense>
                    <ListItem disablePadding>
                        <ListItemButton component={RouterLink} to="/terms" onClick={handleNavClick} sx={navButtonSx}>
                            <ListItemIcon sx={{ minWidth: '40px' }}><GavelIcon fontSize="small" /></ListItemIcon>
                            <ListItemText primary="Terms of Service" primaryTypographyProps={{ variant: 'body2', fontSize: '0.8rem' }} />
                        </ListItemButton>
                    </ListItem>
                    <ListItem disablePadding>
                        <ListItemButton component={RouterLink} to="/privacy" onClick={handleNavClick} sx={navButtonSx}>
                            <ListItemIcon sx={{ minWidth: '40px' }}><PrivacyTipIcon fontSize="small" /></ListItemIcon>
                            <ListItemText primary="Privacy Policy" primaryTypographyProps={{ variant: 'body2', fontSize: '0.8rem' }} />
                        </ListItemButton>
                    </ListItem>
                </List>
                <Divider sx={{ my: 1 }} />
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center' }}>
                    Independent research project. AI-generated content requires verification.
                </Typography>
            </Box>
        </>
    );

    // Mobile: temporary drawer (overlay with backdrop)
    if (isMobile) {
        return (
            <Drawer
                variant="temporary"
                open={mobileOpen}
                onClose={onClose}
                ModalProps={{ keepMounted: true }}
                sx={{
                    [`& .MuiDrawer-paper`]: {
                        width: drawerWidth,
                        boxSizing: 'border-box',
                        display: 'flex',
                        flexDirection: 'column'
                    },
                }}
            >
                {drawerContent}
            </Drawer>
        );
    }

    // Desktop: permanent drawer (unchanged behavior)
    return (
        <Drawer
            variant="permanent"
            sx={{
                width: drawerWidth,
                flexShrink: 0,
                [`& .MuiDrawer-paper`]: {
                    width: drawerWidth,
                    boxSizing: 'border-box',
                    display: 'flex',
                    flexDirection: 'column'
                },
            }}
        >
            {drawerContent}
        </Drawer>
    );
};

export default Sidebar;
